import { getDefinition, type DefLookup } from "./catalog";
import { AC_PROTECTION_PROFILES } from "./catalog/ac-circuits";
import { MULTIPLUS_PRIMARY_PE_MODELS } from "./catalog/charger";
import { rvPanelProtectionProfiles } from "./catalog/rv-distribution";
import { matchesAcSafetyIdentity } from "./diagnostics/ac-safety-identity";
import { buildGraph, splitV, v, type Graph, type Vertex } from "./diagnostics/graph";
import { effectiveTerminals } from "./effective";
import { buildDcCircuitTopology } from "./dc-circuit-topology";
import type { Cable, ComponentDefinition, ComponentNode, System, SystemNode } from "./types";

type TerminalLimits = {
  /** The single-conductor model cannot certify a manufacturer's parallel leads. */
  parallelWiringUnverified?: true;
  minimumWireMm2?: number;
  longRunWireMm2?: number;
  fuse?: { minA: number; maxA: number };
  /** Required manufacturer device; equal amperes do not prove a generic fuse equivalent. */
  manufacturerBreaker?: { ratingA: number };
  /** Manufacturer-supplied conductor, not the contact's thermal rating. */
  suppliedLead?: { wireMm2: number; ampacityA: number };
};

/** A MultiPlus-II primary PE lead needs fault-duty sizing,
 * which a zero-normal-current or endpoint-rating calculation cannot verify.
 * Follow dedicated lead splices/PE buses, but stop where a shared junction or
 * DC negative reference can also serve ordinary modeled return conductors.
 * This establishes uncertainty, not a guessed PE size or an internal bond.
 * https://www.victronenergy.com/media/pg/MultiPlus-II_2x120V/en/installation.html
 */
export function protectiveGroundVerificationCables(system: System, lookup: DefLookup): Set<string> {
  const components = new Map(system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def ? [[node.id, { node, def, terms: effectiveTerminals(def, node) }] as const] : [];
  }));
  const seeds = [...components.values()].filter(({ def, terms }) =>
    MULTIPLUS_PRIMARY_PE_MODELS.has(def.id) &&
    terms.some(term => term.id === "chassis" && term.polarity === "ground"))
    .map(({ node }) => v(node.id, "chassis"));
  const cables = new Set<string>();
  if (!seeds.length) return cables;
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const seen = new Set(seeds), queue = [...seeds];
  for (let i = 0; i < queue.length; i++) {
    for (const { vertex, via } of graph.adj.get(queue[i]) ?? []) {
      if (via.kind === "cable") cables.add(via.cableId);
      if (seen.has(vertex)) continue;
      const { nodeId, terminalId } = splitV(vertex);
      const component = components.get(nodeId);
      if (!component || component.def.category !== "distribution") continue;
      const terminal = component.terms.find(term => term.id === terminalId);
      const dedicatedPe = terminal?.polarity === "ground" &&
        component.terms.every(term => term.polarity === "ground" || term.polarity === "signal");
      const simpleSplice = component.def.renderVariant === "junction" && component.terms.length === 1 &&
        terminal?.polarity === "neutral" &&
        (graph.adj.get(vertex)?.filter(edge => edge.via.kind === "cable").length ?? 0) <= 2;
      if (!dedicatedPe && !simpleSplice) continue;
      seen.add(vertex);
      queue.push(vertex);
    }
  }
  return cables;
}

// Installation rules belong to the exact catalog model and terminal, not its
// display name or generic current rating. Customised catalog definitions retain
// their id; a new custom part does not inherit a guessed manufacturer rule.
const ORION_WIRE = { minimumWireMm2: 16, longRunWireMm2: 25 };
const ORION_POSITIVE = { ...ORION_WIRE, fuse: { minA: 60, maxA: 70 } };
// MSH3012RV Rev E §2.5.1–2.5.2 (p19) and MSH3012RV-L Rev F §2.5.1–2.5.2:
// full-capacity AC input #8 AWG (8.36 mm²), output #6 AWG (13.3 mm²).
// Apply to the named hot/neutral terminals only. This does not establish AC
// operating current, phase/transfer behavior, grounding or protective devices.
// The exact manuals are linked beside the corresponding model entries below.
const MAGNUM_AC_WIRE: Record<string, TerminalLimits> = {
  "ac-in-l1": { minimumWireMm2: 8.36 },
  "ac-in-l2": { minimumWireMm2: 8.36 },
  "ac-in-n": { minimumWireMm2: 8.36 },
  "ac-out-l1": { minimumWireMm2: 13.3 },
  "ac-out-l2": { minimumWireMm2: 13.3 },
  "ac-out-n": { minimumWireMm2: 13.3 },
};
const LIMITS: Record<string, Record<string, TerminalLimits>> = {
  // MSH3012RV Rev E Table 2-1: 4/0 AWG (107.2 mm²), 400 A time-delay
  // protection for rated use. Table 2-2 requires TWO 4/0 cables per polarity
  // beyond 5 ft (1.524 m); the single-conductor model cannot validate that
  // parallel arrangement, so keep the absolute floor and explain the limit.
  // https://www.sensata.com/sites/default/files/a/sensata-msh-3012rv-inverter-charger-manual.pdf
  "shore-magnum-msh3012rv": {
    ...MAGNUM_AC_WIRE,
    "dc-pos": { minimumWireMm2: 107.2, fuse: { minA: 400, maxA: 400 } },
    "dc-neg": { minimumWireMm2: 107.2 },
  },
  // The -L product page links Rev F (July 2026), Tables 2-1/2-2:
  // same 400 A protection and 4/0 AWG minimum within 5 ft one way.
  // https://dmx-power.com/sites/default/files/document/manual/64-0089-W%20F%20Owners%20Manual%2C%20MSH3012RV%20Series.pdf
  "shore-magnum-msh3012rv-l": {
    ...MAGNUM_AC_WIRE,
    "dc-pos": { minimumWireMm2: 107.2, fuse: { minA: 400, maxA: 400 } },
    "dc-neg": { minimumWireMm2: 107.2 },
  },
  // Philippi STV100 manual pp3–4: retain supplied 6 mm² positive lead size,
  // fuse for that lead, and use its 29 A conductor-table rating. The manual
  // does not establish a 29 A terminal-contact or total-panel rating.
  // https://www.philippi-online.de/cdn/shop/files/STV100_V1-DGB.pdf?v=14321852211022057477
  "dist-philippi-stv108": {
    "in-pos": { minimumWireMm2: 6, suppliedLead: { wireMm2: 6, ampacityA: 29 } },
  },
  // EN3DC40+ rev 2.05 p17: vehicle battery 70 A, house battery 60 A.
  // Cable tables vary with voltage/current/length; do not generalize the
  // worked example's 25 mm² into a universal installation minimum.
  // https://enerdrive.com.au/wp-content/uploads/2020/07/DC2DC40-User-Manual_V2.05_JAN-2026-1.pdf
  "dcdc-enerdrive-dc2dc-40": {
    "in-pos": { fuse: { minA: 70, maxA: 70 } },
    "out-pos": { fuse: { minA: 60, maxA: 60 } },
  },
  // MultiPlus-II 230V §4.3, exact 12/3000/120 column. Parallel-conductor
  // requirements cannot be expressed as a single-wire minimum in this model.
  // https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/installation.html
  "shore-victron-multiplus-ii-12-3000-230": {
    "dc-pos": { fuse: { minA: 400, maxA: 400 }, parallelWiringUnverified: true },
    "dc-neg": { parallelWiringUnverified: true },
  },
  // Same DC side in the 120 V and 2×120 V housings: MultiPlus-II 120V §4.3 and
  // MultiPlus-II 2x120V §4.3 both list 400 A for 12/3000/120 (2× 1/0 AWG <5 m).
  // https://www.victronenergy.com/media/pg/MultiPlus-II_120V/en/installation.html
  // https://www.victronenergy.com/media/pg/MultiPlus-II_2x120V/en/installation.html
  "shore-victron-multiplus-3000": {
    "dc-pos": { fuse: { minA: 400, maxA: 400 }, parallelWiringUnverified: true },
    "dc-neg": { parallelWiringUnverified: true },
  },
  // Exact 2x120V manual §4.4 AC terminal table: AWG 6 (13.3 mm²)
  // minimum for AC input and output hot/neutral leads. A smaller inlet
  // breaker does not remove this equipment-terminal minimum. AC protection
  // and grounding remain separate; this does not specify a PE conductor size.
  "shore-victron-multiplus-ii-12-3000-2x120": {
    "ac-in-l1": { minimumWireMm2: 13.3 },
    "ac-in-l2": { minimumWireMm2: 13.3 },
    "ac-in-n": { minimumWireMm2: 13.3 },
    "ac-out-l1": { minimumWireMm2: 13.3 },
    "ac-out-l2": { minimumWireMm2: 13.3 },
    "ac-out-n": { minimumWireMm2: 13.3 },
    "dc-pos": { fuse: { minA: 400, maxA: 400 }, parallelWiringUnverified: true },
    "dc-neg": { parallelWiringUnverified: true },
  },
  // MultiPlus-II 230V §4.3, 48/5000/70 column: 200 A, 70 mm² <5 m, 120 mm² 5–10 m.
  "shore-victron-multiplus-ii-48-5000": {
    "dc-pos": { minimumWireMm2: 70, longRunWireMm2: 120, fuse: { minA: 200, maxA: 200 } },
    "dc-neg": { minimumWireMm2: 70, longRunWireMm2: 120 },
  },
  // Pentair 4008-101-A65 [E65] rev 04/16 p1: recommended fuse 10 A.
  // https://www.pentair.com/content/dam/extranet/web/nam/shurflo/data-sheets/pds-4008-101-X65.pdf
  "load-shurflo-4008": { pos: { fuse: { minA: 10, maxA: 10 } } },
  // Victron Orion XS 12/12-50A installation §3.3: 16 mm² <5 m;
  // 4 AWG (21.2 mm², next metric table size 25 mm²) at 5–10 m; 60–70 A fuse.
  // https://www.victronenergy.com/media/pg/Orion_XS_12-12-50A_DC-DC_battery_charger/en/installation.html
  "dcdc-victron-orion-xs-50": {
    "in-pos": ORION_POSITIVE, "in-neg": ORION_WIRE,
    "out-pos": ORION_POSITIVE, "out-neg": ORION_WIRE,
  },
  // Victron SmartSolar 75/15 installation §4.2: battery fuse 20–25 A.
  // This is an equipment limit, independent of conductor ampacity or PV wiring.
  // https://www.victronenergy.com/media/pg/Manual_SmartSolar_MPPT_75-10_up_to_100-20/en/installation.html
  "mppt-victron-7515": { "batt-pos": { fuse: { minA: 20, maxA: 25 } } },
};

// Lewmar T2388 Issue 3, p12 §4.2 and p30: the 12 V / 700 W Pro-Series
// 1000 uses the supplied 70 A overload breaker. Its motor trip curve must not
// be replaced by the generic surge-hold heuristic or by an arbitrary 70 A fuse.
// https://www.lewmar.com/sites/default/files/Lewmar-Support-Downloads/Product-Literature/Manual-T2388%20Lewmar%20Pro%20Windlass%20Issue%203%20WEB.pdf
// https://www.lippert.com/lewmar-pro-series-1000h-3125in-gypsy-2020201170
const LEWMAR_BREAKER_LIMITS: Record<string, TerminalLimits> = {
  pos: { manufacturerBreaker: { ratingA: 70 } },
};

function installationLimits(node: ComponentNode, def: ComponentDefinition) {
  // A custom clone can retain a catalog id after changing the physical part.
  // This device/curve requirement is deliberately narrower than legacy minima.
  if (node.definitionId === "load-lewmar-1000" && !node.customDefinition &&
    def.id === "load-lewmar-1000" && def.category === "load" &&
    def.manufacturer === "Lewmar" && def.model === "Pro-Series 1000" &&
    def.compatibleVoltages?.length === 1 && def.compatibleVoltages[0] === 12 &&
    (def.voltage == null || def.voltage === 12) &&
    def.terminals.some(t => t.id === "pos" && t.polarity === "positive" && t.direction === "input")) {
    return LEWMAR_BREAKER_LIMITS;
  }
  return LIMITS[def.id];
}

export type InstallationRun = {
  parallelWiringUnverified?: true;
  /** A known manufacturer boundary must not become verified generic sizing. */
  verificationReason?: "ip43-identity" | "ip43-route" | "ip43-length";
  /** Applies only to the first cable at the equipment post, not extensions. */
  terminalWireMaximumMm2?: number;
  minimumWireLabel?: string;
  nodeId: string;
  terminalId: string;
  label: string;
  cableIds: string[];
  minimumWireMm2: number;
  fuse?: { minA: number; maxA: number };
  /** Required manufacturer device; equal amperes do not prove a generic fuse equivalent. */
  manufacturerBreaker?: { ratingA: number; automaticRecommendation: boolean };
  suppliedLead?: { wireMm2: number; ampacityA: number };
};

const terminalPair = (a: string, b: string): string => [a, b].sort().join("|");

/** A panel's reviewed protected branches start separate equipment leads.
 * Cut their hot and associated neutral boundaries, not every connection on
 * the panel: an exposed bus post can still carry an uninterrupted lead.
 * The profiles remain planning declarations, not proof of installed OCPDs. */
function acPanelBoundaries(node: ComponentNode, def: ComponentDefinition): Set<string> {
  const profiles = AC_PROTECTION_PROFILES[def.id] ?? rvPanelProtectionProfiles(def);
  if (!profiles.length || !matchesAcSafetyIdentity(node, def)) return new Set();
  return new Set(profiles.flatMap(({ from, to }) => [
    terminalPair(from, to),
    terminalPair(from.replace(/-l[12]?$/, "-n"), to.replace(/-l[12]?$/, "-n")),
  ]));
}

/** Follow one uninterrupted power lead through inline holders, switches and
 * junctions. Stop at a branch, reviewed AC protection boundary, battery or converter; unrelated circuits on a
 * shared bus must never inherit this device's installation constraints. */
function installationRun(graph: Graph, firstCableId: string, far: Vertex): { cableIds: string[]; end: Vertex } {
  const cables = new Set<string>([firstCableId]);
  let at = far;
  for (;;) {
    const node = graph.nodes.find((n) => n.id === splitV(at).nodeId);
    if (node?.kind !== "component") break;
    const def = graph.defLookup(node.definitionId, node.customDefinition);
    // A voltage-changing device is an endpoint, even if another cable shares
    // its post. Intra-component conductors identify permissible plumbing.
    if (!def || (def.category !== "distribution" && def.category !== "monitor")) break;
    const boundaries = acPanelBoundaries(node, def);
    const group = new Set<Vertex>([at]);
    const queue = [at];
    for (let i = 0; i < queue.length; i++) {
      for (const edge of graph.adj.get(queue[i]) ?? []) {
        if (edge.via.kind !== "intra" || group.has(edge.vertex) ||
          boundaries.has(terminalPair(edge.via.link.from, edge.via.link.to))) continue;
        group.add(edge.vertex);
        queue.push(edge.vertex);
      }
    }
    const exits = new Map<string, Vertex>();
    for (const vertex of group) {
      for (const edge of graph.adj.get(vertex) ?? []) {
        if (edge.via.kind !== "cable" || cables.has(edge.via.cableId)) continue;
        exits.set(edge.via.cableId, edge.vertex);
      }
    }
    if (exits.size !== 1) break;
    const [id, next] = [...exits][0];
    cables.add(id);
    at = next;
  }
  return { cableIds: [...cables], end: at };
}

// Current Smart IP43 120–240 V manual §5.2.1 and §9. The generic 12/30
// catalog entry does not establish this variant; its explicit (1+1) entry does.
// The 4 A auxiliary terminals are not covered by the main-output cable table.
// https://www.victronenergy.com/media/pg/Smart_IP43_Charger_120-240V/en/installation---bsc---psc.html
const IP43_MAIN_PORTS: Record<string, string[]> = {
  "shore-victron-ip43-50": ["batt"],
  "shore-victron-ip43-50-3": ["batt1", "batt2", "batt3"],
  "shore-victron-ip43-30-1plus1": ["batt"],
};

function ip43Identity(node: ComponentNode, def: ComponentDefinition): boolean {
  const original = getDefinition(def.id);
  if (!original || !matchesAcSafetyIdentity(node, def)) return false;
  return (["ampOutput", "ampDraw", "inputVoltage", "outputVoltage", "compatibleVoltages", "outputCurrentLimitsA"] as const)
    .every(key => JSON.stringify(def[key]) === JSON.stringify(original[key]));
}

function ip43Runs(system: System, node: ComponentNode, def: ComponentDefinition, graph: Graph): InstallationRun[] {
  const ports = IP43_MAIN_PORTS[def.id];
  if (!ports) return [];
  const known = ip43Identity(node, def);
  const topology = known ? buildDcCircuitTopology(system, graph.defLookup) : undefined;
  const terms = effectiveTerminals(def, node);
  const cableById = new Map(system.cables.map(cable => [cable.id, cable]));
  const candidates = terms.filter(term => known
    ? ports.some(port => term.id === `${port}-pos` || term.id === `${port}-neg`)
    : term.polarity === "positive" || term.polarity === "negative");
  const traced = candidates.flatMap(term => {
    const leads = (graph.adj.get(v(node.id, term.id)) ?? []).filter(edge => edge.via.kind === "cable");
    return leads.flatMap(edge => {
      if (edge.via.kind !== "cable") return [];
      const route = installationRun(graph, edge.via.cableId, edge.vertex);
      const end = splitV(route.end), endpoint = graph.nodes.find(n => n.id === end.nodeId);
      const endDef = endpoint?.kind === "component" ? graph.defLookup(endpoint.definitionId, endpoint.customDefinition) : undefined;
      const endTerm = endDef && endpoint?.kind === "component" ? effectiveTerminals(endDef, endpoint).find(t => t.id === end.terminalId) : undefined;
      const lengthM = route.cableIds.reduce((sum, id) => sum + (cableById.get(id)?.lengthM ?? NaN), 0);
      return [{ term, ...route, lengthM, resolved: leads.length === 1 && endDef?.category === "battery" &&
        endTerm?.polarity === term.polarity && route.cableIds.every(id => {
          const length = cableById.get(id)?.lengthM;
          return length != null && Number.isFinite(length) && length >= 0;
        }) }];
    });
  });
  return traced.map(route => {
    const pair = route.term.id.replace(/-(?:pos|neg)$/, "");
    const paired = traced.filter(other => other.term.id.replace(/-(?:pos|neg)$/, "") === pair);
    const supply = topology?.resolvePort({ id: `${node.id}:installation:${pair}`, nodeId: node.id,
      positive: [v(node.id, `${pair}-pos`)], negative: [v(node.id, `${pair}-neg`)] });
    const positiveIds = new Set(paired.filter(other => other.term.polarity === "positive").flatMap(other => other.cableIds));
    const negativeIds = new Set(paired.filter(other => other.term.polarity === "negative").flatMap(other => other.cableIds));
    const resolved = known && paired.length === 2 && paired.every(other => other.resolved) &&
      new Set(paired.map(other => other.term.polarity)).size === 2 &&
      !topology?.unsupported.length && !supply?.unsupported.length && !!supply?.routes.some(sourceRoute => {
        const source = sourceRoute.source;
        // Every battery in the paired source must declare the same 12 V-class
        // voltage. The topology's nominal fallback is not rating evidence.
        const voltages = source.batteryNodeIds.map(id => {
          const battery = graph.nodes.find(n => n.id === id);
          return battery?.kind === "component" ? graph.defLookup(battery.definitionId, battery.customDefinition)?.voltage : undefined;
        });
        return source.kind === "battery" && source.levels.length === 1 && voltages.length > 0 &&
          voltages.every(voltage => voltage != null && Number.isFinite(voltage) && voltage >= 10 && voltage <= 15 && voltage === voltages[0]) &&
          // A path may continue past an unrelated battery's negative post.
          // Do not drop that extra return length when the lead tracer stops.
          sourceRoute.positive.length === positiveIds.size && sourceRoute.positive.every(step => positiveIds.has(step.cableId)) &&
          sourceRoute.negative.length === negativeIds.size && sourceRoute.negative.every(step => negativeIds.has(step.cableId));
      });
    // The table assumes equal outgoing and return lengths. Use the longer
    // resolved leg for both; never halve total length or choose a branch.
    const lengthM = Math.max(...paired.map(other => other.lengthM));
    const is30 = def.id === "shore-victron-ip43-30-1plus1";
    const maximumLengthM = is30 ? 4.5 : 3;
    const small = is30 && lengthM <= 3;
    return {
      nodeId: node.id, terminalId: route.term.id, label: node.label ?? def.shortName ?? def.name,
      cableIds: route.cableIds,
      // The manual accepts metric OR AWG sizes. A published AWG area is the
      // numerical floor; metric tables round up to 10/16 without rejecting
      // native AWG8/6 manual wires or forcing AWG4 for area alone.
      minimumWireMm2: !known ? 0 : small ? 8.36 : 13.3,
      minimumWireLabel: known ? small ? "10 mm² / AWG 8" : "16 mm² / AWG 6" : undefined,
      terminalWireMaximumMm2: known ? 16 : undefined,
      verificationReason: !known ? "ip43-identity" : !resolved ? "ip43-route" : lengthM > maximumLengthM ? "ip43-length" : undefined,
    };
  });
}

const runCache = new WeakMap<System, Map<DefLookup, InstallationRun[]>>();

export function computeInstallationRuns(system: System, defLookup: DefLookup): InstallationRun[] {
  let byLookup = runCache.get(system);
  if (!byLookup) {
    byLookup = new Map();
    runCache.set(system, byLookup);
  }
  const cached = byLookup.get(defLookup);
  if (cached) return cached;
  const runs: InstallationRun[] = [];
  let graph: Graph | undefined;
  const cableById = new Map(system.cables.map((c) => [c.id, c]));
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def && IP43_MAIN_PORTS[def.id]) {
      graph ??= buildGraph(system, defLookup, { excludeSense: true });
      runs.push(...ip43Runs(system, node, def, graph));
    }
    const limits = def && installationLimits(node, def);
    if (!def || !limits) continue;
    graph ??= buildGraph(system, defLookup, { excludeSense: true });
    for (const [terminalId, limit] of Object.entries(limits)) {
      if (!def.terminals.some((t) => t.id === terminalId)) continue;
      const leads = (graph.adj.get(v(node.id, terminalId)) ?? []).filter(edge => edge.via.kind === "cable");
      for (const edge of leads) {
        if (edge.via.kind !== "cable") continue;
        const { cableIds } = installationRun(graph, edge.via.cableId, edge.vertex);
        const lengthM = cableIds.reduce((sum, id) => sum + (cableById.get(id)?.lengthM ?? 0), 0);
        runs.push({
          nodeId: node.id, terminalId, cableIds,
          label: node.label ?? def.shortName ?? def.name,
          minimumWireMm2: lengthM >= 5
            ? limit.longRunWireMm2 ?? limit.minimumWireMm2 ?? 0
            : limit.minimumWireMm2 ?? 0,
          fuse: limit.fuse,
          // A device post with several cables is itself a branch or alternate
          // feed. Its equipment breaker must not become a shared-feed rating.
          manufacturerBreaker: limit.manufacturerBreaker ? {
            ...limit.manufacturerBreaker, automaticRecommendation: leads.length === 1,
          } : undefined,
          suppliedLead: limit.suppliedLead,
          parallelWiringUnverified: limit.parallelWiringUnverified,
        });
      }
    }
  }
  byLookup.set(defLookup, runs);
  return runs;
}

type CableInstallationPolicy = {
  minimumWireMm2: number;
  breakerRatings: Set<number>;
};
const policyCache = new WeakMap<
  Record<string, SystemNode>, WeakMap<Cable[], Map<DefLookup, Map<string, CableInstallationPolicy>>>
>();

function cableInstallationPolicies(
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): Map<string, CableInstallationPolicy> {
  let byCables = policyCache.get(nodesById);
  if (!byCables) {
    byCables = new WeakMap();
    policyCache.set(nodesById, byCables);
  }
  let byLookup = byCables.get(cables);
  if (!byLookup) {
    byLookup = new Map();
    byCables.set(cables, byLookup);
  }
  let policies = byLookup.get(defLookup);
  if (!policies) {
    policies = new Map();
    const system: System = { version: 0, name: "", nodes: Object.values(nodesById), cables, zoneDistances: {} };
    for (const run of computeInstallationRuns(system, defLookup)) {
      for (const id of run.cableIds) {
        const policy = policies.get(id) ?? { minimumWireMm2: 0, breakerRatings: new Set<number>() };
        policy.minimumWireMm2 = Math.max(policy.minimumWireMm2, run.minimumWireMm2);
        if (run.manufacturerBreaker?.automaticRecommendation) policy.breakerRatings.add(run.manufacturerBreaker.ratingA);
        policies.set(id, policy);
      }
    }
    byLookup.set(defLookup, policies);
  }
  return policies;
}

/** Per-cable entry point for the shared sizing primitive and all its callers. */
export function installationWireMinimum(
  cableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): number {
  return cableInstallationPolicies(nodesById, cables, defLookup).get(cableId)?.minimumWireMm2 ?? 0;
}

/** Planning rating for an exact manufacturer breaker on an uninterrupted
 * positive lead. This does not establish an installed device or its trip curve.
 * Never choose among conflicting device requirements on a shared conductor. */
export function installationBreakerRating(
  cableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): number | undefined {
  const ratings = cableInstallationPolicies(nodesById, cables, defLookup).get(cableId)?.breakerRatings;
  return ratings?.size === 1 ? [...ratings][0] : undefined;
}
